// frontend/apps/widgets/src/widgets/TableReservation/hooks/useReservationHandlers.js
import {useState, useCallback, useEffect} from 'react';
import {useExternalCreateReservation} from 'packages/common/hooks/api/widgets/table-reservations/Reservations';
import {sendCloseMessage} from '../../common/utils/iframeMessaging';

export function useReservationHandlers({
  formData,
  config,
  currentStep,
  STEPS,
  embedded,
  companyId,
  apiKey,
  goToStep,
  goToNextStep,
  resetSteps,
  setErrors,
  setValidationFunction,
  instanceId = 'default-instance', // Add instanceId with default value
}) {
  const [isStepReady, setIsStepReady] = useState(false);
  const {createReservation, loading: submitLoading} = useExternalCreateReservation();

  // Handle reservation completion
  const handleComplete = useCallback(() => {
    goToStep(STEPS.CONFIRMATION);
  }, [goToStep, STEPS]);

  // Send close event to parent window
  const sendCloseEvent = useCallback(() => {
    if (embedded && window.parent) {
      // Use sendCloseMessage if available, otherwise use direct approach
      if (typeof sendCloseMessage === 'function') {
        sendCloseMessage(instanceId);
      } else {
        // Fallback direct method
        window.parent.postMessage({
          type: 'brydge-close-modal',
          instanceId: instanceId,
        }, '*');
      }
    }
  }, [embedded, instanceId]);

  // Handle widget close
  const handleCloseWidget = useCallback(() => {
    if (embedded) {
      sendCloseEvent();
    }
  }, [embedded, sendCloseEvent]);


  // Reset form
  const handleReset = useCallback(() => {
    resetSteps();
  }, [resetSteps]);

  // Validation function handler
  const provideValidationFunction = useCallback((validationFn) => {
    setValidationFunction(validationFn);
  }, [setValidationFunction]);

  // Error handling
  const handleSetErrors = useCallback((errors) => {
    setErrors(errors);
  }, [setErrors]);

  // Submit reservation
  const handleSubmitReservation = useCallback(async () => {
    try {
      if (currentStep !== STEPS.DETAILS) {
        return;
      }

      const selectedDate = formData.date;
      const [hours, minutes] = formData.timeSlot.split(':').map(Number);

      const dateTime = new Date(selectedDate);
      dateTime.setHours(hours, minutes, 0, 0);

      const duration = config?.globalParameters?.defaultDuration || 120;

      await createReservation(companyId, {
        dateTime: dateTime.toISOString(),
        numberOfGuests: formData.partySize,
        duration,
        customerDetails: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
        },
        specialRequests: formData.specialRequests,
        occasion: formData.occasion,
        source: 'online',
      }, apiKey);

      handleComplete();
    } catch (error) {
      setErrors({api: error.message || 'Une erreur est survenue'});
    }
  }, [currentStep, STEPS.DETAILS, formData, config, companyId, apiKey, createReservation, setErrors, handleComplete]);

  // Next step handler
  const handleNextStep = useCallback(() => {
    if (currentStep === STEPS.DETAILS) {
      handleSubmitReservation();
    } else {
      goToNextStep();
    }
  }, [currentStep, STEPS.DETAILS, handleSubmitReservation, goToNextStep]);

  // Reset step ready state when changing steps
  useEffect(() => {
    setIsStepReady(false);
  }, [currentStep]);

  return {
    isStepReady,
    setIsStepReady,
    submitLoading,
    handleComplete,
    handleCloseWidget,
    handleReset,
    provideValidationFunction,
    handleSetErrors,
    handleNextStep,
  };
}
